import { useMutation, useQuery } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  listQuickLookupsApiV1CosmosQuickLookupsGet,
  queryCosmosApiV1CosmosQueryPost,
} from "../../api/generated/sdk.gen";
import { CosmosQueryRequestSchema } from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

/**
 * Query Cosmos database for information related to a case
 * @returns Mutation for querying Cosmos
 */
export const useCosmosQuery = () => {
  return useMutation({
    mutationFn: async (data: CosmosQueryRequestSchema) => {
      const response = await queryCosmosApiV1CosmosQueryPost({
        client: apiClient,
        body: data,
      });

      // Check for errors in the response
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to query Cosmos", response.error)
        );
      }

      return response.data;
    },
  });
};

/**
 * The enabled Quick Lookups and the pack's boundary line, from the
 * cosmos-service register. The wording is owned there, so the frontend never
 * keeps its own copy. It only changes on a service deploy, hence the long
 * stale time.
 */
export const useQuickLookups = (enabled: boolean) => {
  return useQuery({
    queryKey: [CLIENT_KEYS.COSMOS, "quick-lookups"],
    queryFn: async () => {
      const response = await listQuickLookupsApiV1CosmosQuickLookupsGet({
        client: apiClient,
      });
      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to load Quick Lookups", response.error)
        );
      }
      return response.data;
    },
    enabled,
    staleTime: 60 * 60 * 1000,
  });
};
